import { AuthError, AuthProvider, AuthCredential, AdditionalUserInfo, OAuthProvider } from 'firebase/auth'
import { assign, createMachine, DoneInvokeEvent, Interpreter } from 'xstate'

export type Mode = 'register' | 'login'
export type CopyContext =
  | 'clap'
  | 'newsletter'
  | 'reviews'
  | 'lists'
  | 'lists-want-to-test'
  | 'lists-tested'
  | 'submit-brand'
  | 'thingdrops'
  | 'magazine'
  | 'sampling'
  | null
type OnSuccessFunctionOrKeyword = ((ctx: AuthContext) => void) | 'onboardingOrHome' | 'doNothing' | null

const defaultOnSuccess: OnSuccessFunctionOrKeyword = 'onboardingOrHome'

export type AuthContext = {
  modalOpen: boolean
  mode: Mode
  provider?: AuthProvider | null
  pendingCredential?: AuthCredential | null
  email?: string | null
  password?: string | null
  error?: string | null
  copyContext?: CopyContext
  onSigninSuccess?: OnSuccessFunctionOrKeyword
  analyticsContext?: string | null
  additionalUserInfo?: AdditionalUserInfo
}

type AuthState =
  | {
      value: 'initializing'
      context: AuthContext
    }
  | {
      value: 'ready'
      context: AuthContext
    }
  | {
      value: 'signedIn'
      context: AuthContext
    }
  | {
      value: 'completingEmailSignIn'
      context: AuthContext
    }
  | {
      value: 'signingOut'
      context: AuthContext
    }
  | {
      value: 'formVisible'
      context: AuthContext
    }
  | {
      value: 'readyToSignIn'
      context: AuthContext
    }
  | {
      value: 'signingInWithProvider'
      context: AuthContext & { provider: AuthProvider }
    }
  | {
      value: 'signingInWithEmail'
      context: AuthContext & { email: string }
    }
  | {
      value: 'gettingSigninProvidersForEmail'
      context: AuthContext & { provider: AuthProvider }
    }
  | {
      value: 'readyToSignInWithExistingProvider'
      context: AuthContext & { email: string; pendingCredential: AuthProvider; provider: AuthProvider }
    }
  | {
      value: 'signingInWithExistingProvider'
      context: AuthContext & { email: string; pendingCredential: AuthProvider; provider: AuthProvider }
    }
  | {
      value: 'emailLinkSent'
      context: AuthContext
    }
  | {
      value: 'runningSuccessfulSigninCallbacks'
      context: AuthContext & { additionalUserInfo: AdditionalUserInfo }
    }

type ShowModalAction = {
  type: 'SHOW_MODAL'
  mode?: Mode
  copyContext?: CopyContext
  onSigninSuccess?: OnSuccessFunctionOrKeyword
  analyticsContext?: string
}
type HideModalAction = { type: 'HIDE_MODAL' }
type SignInWithProviderAction = {
  type: 'SIGN_IN_WITH_PROVIDER'
  provider: AuthProvider
  analyticsContext?: string
  onSigninSuccess?: OnSuccessFunctionOrKeyword
}
type SignInWithEmailAction = { type: 'SIGN_IN_WITH_EMAIL'; email: string; analyticsContext?: string }
type SignInWithEmailAndPasswordAction = { type: 'SIGN_IN_WITH_EMAIL_AND_PASSWORD'; email: string; password: string; analyticsContext?: string }
type SignOutAction = { type: 'SIGN_OUT' }
type CompleteEmailSignIn = { type: 'COMPLETE_EMAIL_SIGN_IN' }
type ChangeModeAction = { type: 'CHANGE_MODE'; mode: Mode }
type SignInWithExistingProviderAction = { type: 'SIGN_IN_WITH_EXISTING_PROVIDER' }
type StartOverAction = { type: 'START_OVER' }

type AuthEvent =
  | ShowModalAction
  | HideModalAction
  | SignInWithProviderAction
  | SignInWithEmailAction
  | SignInWithEmailAndPasswordAction
  | SignOutAction
  | CompleteEmailSignIn
  | ChangeModeAction
  | SignInWithExistingProviderAction
  | StartOverAction

export type AuthService = Interpreter<AuthContext, any, AuthEvent, AuthState>
export type DoneGettingUser = DoneInvokeEvent<{ isSignedIn: boolean }>

export enum Tags {
  Disabled = 'Disabled',
  Loading = 'Loading'
}

export const authenticationMachine = createMachine<AuthContext, AuthEvent, AuthState>(
  {
    id: 'authenticationMachine',
    initial: 'initializing',
    context: {
      provider: null,
      email: null,
      modalOpen: false,
      mode: 'register',
      error: null,
      onSigninSuccess: defaultOnSuccess
    },
    on: {
      SHOW_MODAL: {
        target: 'ready',
        actions: ['showModal', 'trackModalState']
      },
      HIDE_MODAL: {
        actions: ['hideModal', 'trackModalState', 'resetContext']
      },
      CHANGE_MODE: {
        actions: ['changeMode', 'trackModalState', 'clearError']
      },
      START_OVER: {
        target: 'ready',
        actions: ['resetContext']
      },
      COMPLETE_EMAIL_SIGN_IN: {
        target: 'completingEmailSignIn'
      }
    },
    states: {
      initializing: {
        tags: [Tags.Disabled],
        invoke: {
          id: 'getUser',
          src: 'getLoginState',
          onDone: [
            {
              target: 'signedIn',
              cond: (_, { data }: DoneGettingUser) => data.isSignedIn
            },
            {
              target: 'ready',
              cond: (_, { data }: DoneGettingUser) => !data.isSignedIn
            }
          ],
          onError: {
            target: 'ready',
            actions: ['logUnexpectedFailureFromMachine']
          }
        }
      },
      ready: {
        on: {
          SIGN_IN_WITH_PROVIDER: {
            target: 'signingInWithProvider',
            actions: ['setProvider']
          },
          SIGN_IN_WITH_EMAIL: {
            target: 'signingInWithEmail',
            actions: ['setEmail']
          },
          SIGN_IN_WITH_EMAIL_AND_PASSWORD: {
            target: 'signingInWithEmailAndPassword',
            actions: ['setEmail', 'setPassword']
          }
        }
      },
      signingInWithEmail: {
        tags: [Tags.Loading],
        entry: ['clearError'],
        invoke: {
          id: 'signInWithEmail',
          src: 'signInWithEmail',
          onDone: {
            target: 'emailLinkSent'
          },
          onError: {
            target: 'ready',
            actions: ['setError']
          }
        }
      },
      signingInWithEmailAndPassword: {
        tags: [Tags.Loading],
        entry: ['clearError'],
        invoke: {
          id: 'signInWithEmailAndPassword',
          src: 'signInWithEmailAndPassword',
          onDone: {
            target: 'runningSuccessfulSigninCallbacks',
            actions: ['setAdditionalUserInfo']
          },
          onError: {
            target: 'ready',
            actions: ['setError']
          }
        }
      },
      emailLinkSent: {
        on: {
          SIGN_IN_WITH_PROVIDER: {
            target: 'signingInWithProvider',
            actions: ['setProvider']
          },
          SIGN_IN_WITH_EMAIL: {
            target: 'signingInWithEmail',
            actions: ['setEmail']
          }
        }
      },
      signingInWithProvider: {
        tags: [Tags.Loading],
        entry: ['clearError'],
        invoke: {
          id: 'signInWithProvider',
          src: 'signInWithProvider',
          onDone: {
            target: 'runningSuccessfulSigninCallbacks',
            actions: ['setAnalyticsContext', 'setOnSigninSuccess', 'setAdditionalUserInfo']
          },
          onError: [
            {
              target: 'ready',
              cond: (_, { data }: { data: AuthError }) => data.code !== 'auth/account-exists-with-different-credential',
              actions: ['setError']
            },
            {
              target: 'gettingSigninProvidersForEmail',
              cond: (_, { data }: { data: AuthError }) => data.code === 'auth/account-exists-with-different-credential',
              actions: ['setPendingCredential']
            }
          ]
        }
      },
      readyToSignInWithExistingProvider: {
        on: {
          SIGN_IN_WITH_EXISTING_PROVIDER: {
            target: 'signingInWithExistingProvider'
          }
        }
      },
      signingInWithExistingProvider: {
        tags: [Tags.Loading],
        entry: ['clearError'],
        invoke: {
          id: 'signInWithExistingProvider',
          src: 'signInWithExistingProvider',
          onDone: {
            target: 'runningSuccessfulSigninCallbacks',
            actions: ['setAdditionalUserInfo']
          },
          onError: {
            target: 'ready',
            actions: ['setError']
          }
        }
      },
      gettingSigninProvidersForEmail: {
        tags: [Tags.Loading],
        invoke: {
          id: 'getSigninProvidersForEmail',
          src: 'getSigninProvidersForEmail',
          onDone: {
            target: 'readyToSignInWithExistingProvider',
            actions: ['setProviderFromInvokeEvent']
          },
          onError: {
            target: 'ready',
            actions: ['setError']
          }
        }
      },
      completingEmailSignIn: {
        tags: [Tags.Loading],
        entry: ['clearError'],
        invoke: {
          id: 'completeEmailSignIn',
          src: 'completeEmailSignIn',
          onDone: {
            target: 'runningSuccessfulSigninCallbacks',
            actions: ['setAdditionalUserInfo']
          },
          onError: {
            target: 'ready',
            actions: ['setError']
          }
        }
      },
      runningSuccessfulSigninCallbacks: {
        tags: [Tags.Loading, Tags.Disabled],
        invoke: {
          id: 'runSuccessfulSigninCallbacks',
          src: 'runSuccessfulSigninCallbacks',
          onDone: {
            target: 'signedIn'
          }
        }
      },
      signedIn: {
        tags: [Tags.Disabled],
        entry: ['hideModal'],
        invoke: {
          id: 'getRedirectResult',
          src: 'getRedirectResult',
          onDone: {
            actions: ['setAdditionalUserInfo']
          },
          onError: {
            actions: ['logUnexpectedFailureFromMachine']
          }
        },
        on: {
          SIGN_OUT: {
            target: 'signingOut'
          },
          // Allow email links to be re-sent to reauthenticate the session for sensitive actions such as email changes
          SIGN_IN_WITH_EMAIL: {
            target: 'signingInWithEmail',
            actions: ['setEmail']
          }
        }
      },
      signingOut: {
        tags: [Tags.Disabled],
        entry: ['resetContext'],
        invoke: {
          id: 'signOut',
          src: 'signOut',
          onDone: {
            target: 'ready'
          },
          onError: {
            actions: ['logUnexpectedFailureFromMachine']
          }
        }
      }
    }
  },
  {
    actions: {
      showModal: assign<AuthContext, ShowModalAction>({
        modalOpen: true,
        mode: ({ mode: currentMode }, { mode: newMode }) => newMode ?? currentMode,
        copyContext: (_, { copyContext }) => copyContext ?? null,
        onSigninSuccess: (_, { onSigninSuccess }) => onSigninSuccess ?? defaultOnSuccess,
        analyticsContext: (_, { analyticsContext }) => analyticsContext ?? null
      }) as any,

      hideModal: assign<AuthContext, HideModalAction>({
        modalOpen: false
      }) as any,

      setProvider: assign<AuthContext, SignInWithProviderAction>({
        provider: (_, { provider }) => provider,
        analyticsContext: (old, { analyticsContext }) => analyticsContext ?? old.analyticsContext,
        onSigninSuccess: (old, { onSigninSuccess }) => onSigninSuccess ?? old.onSigninSuccess
      }) as any,

      setProviderFromInvokeEvent: assign<AuthContext, DoneInvokeEvent<AuthProvider>>({
        provider: (_, { data }) => data
      }) as any,

      setEmail: assign<AuthContext, SignInWithEmailAction>({
        email: (_, { email }) => email,
        analyticsContext: ({ analyticsContext: currentAnalyticsContext }, { analyticsContext }) => analyticsContext ?? currentAnalyticsContext
      }) as any,

      setPassword: assign<AuthContext, SignInWithEmailAndPasswordAction>({
        password: (_, { password }) => password,
        analyticsContext: ({ analyticsContext: currentAnalyticsContext }, { analyticsContext }) => analyticsContext ?? currentAnalyticsContext
      }) as any,

      changeMode: assign<AuthContext, ChangeModeAction>({
        mode: (_, { mode }) => mode
      }) as any,

      resetContext: assign<AuthContext>({
        provider: null,
        pendingCredential: null,
        email: null,
        error: null,
        analyticsContext: null,
        onSigninSuccess: defaultOnSuccess
      }) as any,

      setPendingCredential: assign<AuthContext, DoneInvokeEvent<AuthError>>({
        email: (_, { data }) => data.customData.email,
        pendingCredential: (_, { data }) => OAuthProvider.credentialFromError(data)
      }) as any,

      setError: assign<AuthContext, DoneInvokeEvent<Error>>({
        error: (_, { data }) => (data.message === '' ? null : data.message)
      }) as any,

      clearError: assign<AuthContext>({
        error: null
      }) as any,

      setAdditionalUserInfo: assign<AuthContext, DoneInvokeEvent<AdditionalUserInfo>>({
        additionalUserInfo: (_, { data }) => data
      }) as any
    }
  }
)
